import React from "react";
import { Navigate } from "react-router-dom";

const norm = (s) => (s || "").toLowerCase().replace(/[^a-z]/g, "");

function getAllRoles() {
  const rolee = localStorage.getItem("ROLE");
  let userRoles = [];
  try {
    userRoles = JSON.parse(localStorage.getItem("ACCESSES") || "[]")
      .flatMap((acc) => (Array.isArray(acc.roles) ? acc.roles : []))
      .map((r) => (typeof r === "string" ? r : r?.role));
  } catch {}
  return [...(rolee ? [rolee] : []), ...(userRoles.length ? userRoles : [])];
}

// Users who should always be let through regardless of role/blockedRoles
// (add more usernames/emails here as needed, lowercase, comma separated)
const PROTECTED_ROUTE_WHITELISTED_USERS = ["vivek.gupta"];

function getCurrentUsername() {
  try {
    const raw = localStorage.getItem("USER_DATA");
    if (raw) {
      const data = JSON.parse(raw);
      const candidates = [data?.username, data?.user_name, data?.email, data?.name].filter(Boolean);
      return candidates.map((v) => String(v).toLowerCase());
    }
  } catch {}
  return [];
}

/**
 * Wrap a route element with this to restrict it to a set of roles.
 *
 * Usage:
 * <Route
 *   path="/project-head/dashboard"
 *   element={
 *     <ProtectedRoute allowedRoles={["project head", "head"]} blockedRoles={["checker", "maker"]}>
 *       <ProjectHeadDashboard />
 *     </ProtectedRoute>
 *   }
 * />
 */
function ProtectedRoute({
  children,
  allowedRoles = [],
  blockedRoles = [],
  redirectTo = "/config",
}) {
  const allRoles = getAllRoles();

  const hasAllowedRole =
    allowedRoles.length === 0 ||
    allowedRoles.some((allowed) =>
      allRoles.some((r) => norm(r) === norm(allowed))
    );

  const hasBlockedRole = blockedRoles.some((blocked) =>
    allRoles.some((r) => norm(r) === norm(blocked))
  );

  // A blocked role should only stop someone who doesn't ALSO hold an allowed
  // role. Otherwise a multi-role user (e.g. Project Head who is also a
  // Checker) gets wrongly locked out even though they legitimately qualify
  // via the allowed role.
  const blockedWithoutAllowedRole = hasBlockedRole && !hasAllowedRole;

  // Named-user override (e.g. vivek.gupta) — always let these users through.
  const isWhitelistedUser = getCurrentUsername().some((id) =>
    PROTECTED_ROUTE_WHITELISTED_USERS.includes(id)
  );

  if (isWhitelistedUser) {
    return children;
  }

  if (!hasAllowedRole || blockedWithoutAllowedRole) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

export default ProtectedRoute;